import { defineStore } from 'pinia'
import { Order } from '@/globals/typescript/models/Order'
import type {
  IDhlShipment,
  IOrderActivityLogComment,
  ISampleDescriptionOverride,
  TOrderPriority,
  TOrderSegment,
} from '@team-milva/lab-models/resources'
import type { IAddress } from '@team-milva/lab-models'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { ref as storageRef } from 'firebase/storage'
import { db, storage } from '@/firebase/init'
import { deleteStorageFolder } from '@/globals/typescript/utils/deleteStorageFolder'
import { resetOrderNumbering } from '@/globals/typescript/utils/orderNumbering'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { toRefusalError } from '@/globals/typescript/utils/apiRefusal'
import type {
  TChangeOrderCustomerBody,
  TChangeOrderCustomerResult,
} from '@/globals/typescript/utils/orderCustomerChange'
import { useAccountStore } from '@/stores/accountStore'
import { useUserStore } from '@/stores/userStore'

type SampleInput = {
  analysisIDs: string[]
  sampleCode: string
  sampleAreaID: string
  tatID: string
  builtYear?: number
  buildingLocation?: string
  material?: string
  additionalNotes?: string
}

// The top-level order.dropPoint snapshot only ever represents a lab/private
// drop point (matches IOrderDropPointSnapshot) — a DHL service point is
// carried on sampleHandOver.dropPoint only, never here.
type LabOrPrivateDropPointSnapshot = {
  id: string
  type: 'lab' | 'private'
  title: string
  address: { addressLine: string; postalCode: string; city: string }
  placement: string
}

type DropPointSnapshot =
  | LabOrPrivateDropPointSnapshot
  | {
      type: 'dhl-service-point'
      servicePointID: string
      name: string
      address: {
        addressLine: string
        city: string
        postalCode: string
        countryCode: string
      }
    }

type CreateOrderParams = {
  segment: TOrderSegment
  priority: TOrderPriority
  caseNumber: string
  address: IAddress
  isInsuranceCase: boolean
  insurancePartnerID: string | null
  insuranceNumber: string | null
  customerAccountID: string
  /** Set only for an insurance customer's own order (MIL-402), with `customerAccountID: ''`. */
  insuranceCustomerAccountID?: string
  customerUserID: string
  privateUserID: string
  labPrefix: string
  samples: SampleInput[]
  dropPoint: LabOrPrivateDropPointSnapshot | null
  sampleHandOver: {
    method: 'drop-point'
    dropPoint: DropPointSnapshot
    dropoff: { date: string; hours: number; minutes: number }
    pickup: { date: string; hours: number; minutes: number }
  }
}

export const useOrderStore = defineStore('order', {
  state: () => ({
    allOrders: [] as Order[],
    unsubscribeAllOrders: null as (() => void) | null,
    loadingSampleActionIDs: [] as string[],
  }),

  getters: {
    allOrdersLoaded: (state): boolean => !!state.unsubscribeAllOrders,
  },

  actions: {
    getOrders(): Promise<Order[]> {
      if (this.allOrdersLoaded) {
        return Promise.resolve(this.allOrders)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Orders'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          // Draft orders (created but not yet placed via lab-customer) are
          // excluded here, centrally — this is the one place every LM order
          // list/search/view reads from, so nothing downstream needs its own
          // filter. See the "Lab Customer — Create Order & Samples" plan.
          this.allOrders = querySnapshot.docs
            .map((docSnap) => new Order(docSnap.data()))
            .filter((order) => order.statuses.orderStatus !== 'draft')

          if (!resolved) {
            resolved = true
            resolve(this.allOrders)
          }
        })

        this.unsubscribeAllOrders = unsubscribe
      })
    },

    // Hydrates from a Display's public data endpoint — a plain JSON payload,
    // not an onSnapshot listener. Never sets unsubscribeAllOrders, so
    // allOrdersLoaded stays accurate for the normal authenticated flow.
    hydrateFromPublicData(raw: unknown[]): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.allOrders = (raw as any[]).map((x) => new Order(x))
    },

    resetStore(): void {
      this.allOrders = []
      this.loadingSampleActionIDs = []

      if (this.unsubscribeAllOrders) {
        this.unsubscribeAllOrders()
        this.unsubscribeAllOrders = null
      }
    },

    async generateOrderPrefix(): Promise<string> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) throw new Error('No lab account loaded')

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/generate-prefix',
        { path: { labAccountID } }
      )

      if (!result.success) throw new Error(result.code)
      return result.data.prefix
    },

    async uncancelSample(orderID: string, sampleID: string): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(sampleID)
      try {
        const result = await managementApi(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/uncancel',
          {
            path: {
              labAccountID: accountStore.labAccount?.id ?? '',
              orderID,
              sampleID,
            },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => x !== sampleID
        )
      }
    },

    async cancelSample(orderID: string, sampleID: string): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(sampleID)
      try {
        const result = await managementApi(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/cancel',
          {
            path: {
              labAccountID: accountStore.labAccount?.id ?? '',
              orderID,
              sampleID,
            },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => x !== sampleID
        )
      }
    },

    async cancelAnalysis(
      orderID: string,
      sampleID: string,
      analysisID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analyses/{analysisID}/cancel',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
            sampleID,
            analysisID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async uncancelAnalysis(
      orderID: string,
      sampleID: string,
      analysisID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analyses/{analysisID}/uncancel',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
            sampleID,
            analysisID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async unregisterSample(orderID: string, sampleID: string): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(sampleID)
      try {
        const result = await managementApi(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/unregister',
          {
            path: {
              labAccountID: accountStore.labAccount?.id ?? '',
              orderID,
              sampleID,
            },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => x !== sampleID
        )
      }
    },

    async updateSampleLocation(
      orderID: string,
      sampleID: string,
      currentLocationID: string | null
    ): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(sampleID)
      try {
        // TODO: remove cast after deploying and re-running openapi:gen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)(
          'patch',
          'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/location',
          {
            path: {
              labAccountID: accountStore.labAccount?.id ?? '',
              orderID,
              sampleID,
            },
            body: { currentLocationID },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => x !== sampleID
        )
      }
    },

    async forwardSamples(
      orderID: string,
      sampleIDs: string[],
      fromLocationID: string,
      toLocationID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(...sampleIDs)
      try {
        // TODO: remove cast after deploying and re-running openapi:gen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/samples/forward',
          {
            path: { labAccountID: accountStore.labAccount?.id ?? '', orderID },
            body: { sampleIDs, fromLocationID, toLocationID },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => !sampleIDs.includes(x)
        )
      }
    },

    async unforwardSamples(orderID: string, sampleIDs: string[]): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(...sampleIDs)
      try {
        // TODO: remove cast after deploying and re-running openapi:gen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/samples/unforward',
          {
            path: { labAccountID: accountStore.labAccount?.id ?? '', orderID },
            body: { sampleIDs },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => !sampleIDs.includes(x)
        )
      }
    },

    async recalculateOrderStatuses(orderID: string): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/recalculate',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    /**
     * DEV: copies an order into a new one with its own order number (see `duplicateOrderService` in
     * lab-cloud). Resolves once the copy has arrived in `allOrders`, so a caller can navigate to it
     * straight away instead of landing on a page for an order the live list has not delivered yet.
     */
    async duplicateOrder(orderID: string): Promise<{ orderID: string; orderNumber: string }> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/dev-duplicate',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
      const copy = result.data
      for (let attempt = 0; attempt < 30; attempt++) {
        if (this.allOrders.some((x) => x.id === copy.orderID)) break
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
      return copy
    },

    async recalculateOrderPricing(orderID: string): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/recalculate-pricing',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async addOrderAddonLine(orderID: string, addonID: string): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/addon-lines/{addonID}',
        {
          path: { labAccountID, orderID, addonID },
        }
      )
      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async removeOrderAddonLine(orderID: string, addonID: string): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'delete',
        'lab-accounts/{labAccountID}/orders/{orderID}/addon-lines/{addonID}',
        {
          path: { labAccountID, orderID, addonID },
        }
      )
      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async cancelOrder(orderID: string, reason?: string): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/cancel',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
          body: { reason },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async cancelOrderSamples(
      orderID: string,
      sampleIDs: string[],
      reason?: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/cancel-samples',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
          body: { sampleIDs, reason },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    /**
     * Flips an order's internal state after creation. The backend refuses once an invoice
     * is attached or the order is cancelled — the menu hides the action in both cases, so
     * a failure here means the state changed under the user, not a normal outcome.
     */
    async setOrderInternal(orderID: string, isInternal: boolean): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/internal',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
          body: { isInternal },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    /**
     * Changes who an order belongs to — the customer account and/or the user (MIL-608). With
     * `dryRun: true` it only returns what would change, so the dialog can show it before the lab
     * confirms; the same shape comes back either way. A refusal (an invoice locks the order, it is
     * cancelled) carries a sentence for the user — see `apiRefusal`.
     */
    async changeOrderCustomer(
      orderID: string,
      body: TChangeOrderCustomerBody
    ): Promise<TChangeOrderCustomerResult> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/change-customer',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
          body,
        }
      )
      if (!result.success) throw toRefusalError(result)
      return result.data
    },

    async reopenOrder(orderID: string): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/reopen',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async registerSample(
      orderID: string,
      sampleID: string,
      tatStartDate?: number
    ): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(sampleID)
      try {
        const result = await managementApi(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/register',
          {
            path: {
              labAccountID: accountStore.labAccount?.id ?? '',
              orderID,
              sampleID,
            },
            body: { tatStartDate },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => x !== sampleID
        )
      }
    },

    async registerSamples(
      orderID: string,
      sampleIDs: string[],
      tatStartDate?: number,
      locationID?: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      this.loadingSampleActionIDs.push(...sampleIDs)
      try {
        // TODO: remove cast after deploying and re-running openapi:gen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)(
          'post',
          'lab-accounts/{labAccountID}/orders/{orderID}/register-samples',
          {
            path: { labAccountID: accountStore.labAccount?.id ?? '', orderID },
            body: { sampleIDs, tatStartDate, locationID },
          }
        )
        if (!result.success) throw new Error(result.code)
      } finally {
        this.loadingSampleActionIDs = this.loadingSampleActionIDs.filter(
          (x) => !sampleIDs.includes(x)
        )
      }
    },

    async registerAllSamples(orderID: string): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/register-all-samples',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderTatStartDate(
      orderID: string,
      tatStartDate: number
    ): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/tat-start-date',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
          body: { tatStartDate },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    // targetPickupDate lets the caller pick a new pickup date when the stored
    // one already missed its DHL booking cutoff — retrying with the same date
    // would just fail again with the same cutoff error.
    async retryDhlShipment(
      orderID: string,
      targetPickupDate?: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/shipment',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            orderID,
          },
          body: { targetPickupDate },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    // DEV ONLY — deletes all orders, samples, and invoices for the current lab account,
    // plus their Storage files (reports, invoices, Bilag order attachments). Does NOT touch
    // BilagLibraryItems — those are lab-scoped, not order-scoped, and have no cleanup path
    // here by design.
    async deleteAllOrdersAndSamples(): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const [ordersSnap, samplesSnap, analysisResultsSnap, invoicesSnap] =
        await Promise.all([
          getDocs(
            query(
              collection(db, 'Apps', '-lab-customer-', 'Orders'),
              where('labAccountID', '==', labAccountID)
            )
          ),
          getDocs(
            query(
              collection(db, 'Apps', '-lab-customer-', 'Samples'),
              where('labAccountID', '==', labAccountID)
            )
          ),
          getDocs(
            query(
              collection(db, 'Apps', '-lab-customer-', 'AnalysisResults'),
              where('labAccountID', '==', labAccountID)
            )
          ),
          getDocs(
            query(
              collection(db, 'Apps', '-lab-customer-', 'Invoices'),
              where('labAccountID', '==', labAccountID)
            )
          ),
        ])

      const batch = writeBatch(db)
      ordersSnap.docs.forEach((x) => batch.delete(x.ref))
      samplesSnap.docs.forEach((x) => batch.delete(x.ref))
      analysisResultsSnap.docs.forEach((x) => batch.delete(x.ref))
      invoicesSnap.docs.forEach((x) => batch.delete(x.ref))

      await batch.commit()

      // Reset the numbering too, not just the records. Without this a cleared lab keeps
      // counting from wherever the last run reached — the seed runner's "Ryd alt" has always
      // done this, and this path silently did not until 2026-09-08. Shared so they cannot
      // drift again.
      await resetOrderNumbering(labAccountID)

      // Best-effort: report/invoice PDFs are server-write-only under storage.rules, so a client
      // delete here can permission-deny once those rules are enforced — that must never block
      // the Firestore cleanup above, which is this action's actual purpose.
      await Promise.all([
        deleteStorageFolder(storageRef(storage, `labAccounts/${labAccountID}/orders`)).catch(() => {}),
        deleteStorageFolder(storageRef(storage, `labAccounts/${labAccountID}/invoices`)).catch(() => {}),
      ])
    },

    // Server-side: recomputes pricing from scratch and runs the DHL capability
    // pre-check + booking before committing anything — see createOrderService in
    // lab-cloud. The DHL booking (QR code / label) is now synchronous, so the
    // response already carries the resolved shipment — callers no longer need to
    // wait on the Firestore listener in dhlShipmentStore to see it.
    async createOrder(
      params: CreateOrderParams
    ): Promise<{ orderID: string; dhlShipment: IDhlShipment | null; sampleIDs: string[] }> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi('post', 'lab-accounts/{labAccountID}/orders', {
        path: { labAccountID },
        body: params,
      })

      if (!result.success) throw new Error(result.message ?? result.code)
      // TODO: remove cast after deploying and re-running openapi:gen — codegen still reflects
      // IDhlShipment's pre-nested-timestamps shape, and doesn't yet know about sampleIDs.
      return result.data as unknown as {
        orderID: string
        dhlShipment: IDhlShipment | null
        sampleIDs: string[]
      }
    },

    /**
     * Approves a delivery. `mode: 'hold'` generates and freezes the PDFs without sending
     * anything, parking the delivery until it is sent or marked as handled manually.
     * `mode: 'schedule'` does the same but lets the daily job release it on its delivery day.
     */
    async approveOrderReports(
      orderID: string,
      tatGroupID: string,
      mode: 'send' | 'hold' | 'schedule' = 'send'
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/approve-and-generate-reports',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, mode },
        }
      )

      if (!result.success) throw new Error(result.code)
    },

    /** Sends a delivery that was approved earlier without being sent. */
    async sendApprovedOrderReports(
      orderID: string,
      tatGroupID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/send-approved-reports',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID },
        }
      )

      if (!result.success) throw new Error(result.code)
    },

    /**
     * Schedules a delivery that was approved earlier without being sent. No PIN and no new PDFs —
     * the report was signed at approval, and the PDFs frozen then are what the daily job sends.
     */
    async scheduleApprovedOrderReports(
      orderID: string,
      tatGroupID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/schedule-approved-reports',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID },
        }
      )

      if (!result.success) throw new Error(result.code)
    },

    /** Calls off a scheduled send, returning the delivery to approved-but-unsent. */
    async cancelScheduledOrderReports(
      orderID: string,
      tatGroupID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/cancel-scheduled-reports',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID },
        }
      )

      if (!result.success) throw new Error(result.code)
    },

    /** Records that the lab delivered an approved report outside the system. */
    async markOrderReportsSentManually(
      orderID: string,
      tatGroupID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/mark-reports-sent-manually',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID },
        }
      )

      if (!result.success) throw new Error(result.code)
    },

    // DEV ONLY — approves reports but intentionally fails PDF generation to test the sent-failed state
    async approveOrderReportsDevFail(
      orderID: string,
      tatGroupID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/approve-and-generate-reports',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, _devForceFailPdf: true },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderReportTemplateAssignment(
      orderID: string,
      key: string,
      reportTemplateID: string | null
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-template-assignments',
        {
          path: { labAccountID, orderID },
          body: { key, reportTemplateID },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderReportResponsibleUserAssignment(
      orderID: string,
      key: string,
      userID: string | null
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-responsible-user-assignments',
        {
          path: { labAccountID, orderID },
          body: { key, userID },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderTatGroup(
      orderID: string,
      tatGroupID: string,
      tatID: string,
      mergeTatGroupID?: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/tat-groups/{tatGroupID}/tat',
        {
          path: { labAccountID, orderID, tatGroupID },
          body: {
            tatID,
            ...(mergeTatGroupID ? { mergeTatGroupID } : {}),
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderReportDescriptionSelections(
      orderID: string,
      tatGroupID: string,
      reportTemplateID: string,
      itemIDs: string[]
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-description-selections',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, reportTemplateID, itemIDs },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderReportSampleDescriptionOverride(
      orderID: string,
      tatGroupID: string,
      reportTemplateID: string,
      sampleID: string,
      override: ISampleDescriptionOverride
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-sample-description-overrides',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, reportTemplateID, sampleID, override },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderReportOrderDescriptions(
      orderID: string,
      tatGroupID: string,
      reportTemplateID: string,
      text: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-order-descriptions',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, reportTemplateID, text },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderDetails(
      orderID: string,
      details: {
        address?: IAddress
        caseNumber?: string
        isInsuranceCase?: boolean
        insurancePartnerID?: string | null
        insuranceNumber?: string | null
        finalInvoicePayer?: 'partner' | 'customer' | 'split' | null
      }
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/details',
        {
          path: { labAccountID, orderID },
          body: details,
        }
      )
      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async updateSampleDetails(
      orderID: string,
      sampleID: string,
      details: {
        builtYear?: number
        buildingLocation?: string
        material?: string
        additionalNotes?: string
      }
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/details',
        {
          path: { labAccountID, orderID, sampleID },
          body: details,
        }
      )
      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async updateOrderReportRecipients(
      orderID: string,
      reportRecipientEmails: string[]
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-recipients',
        {
          path: { labAccountID, orderID },
          body: { reportRecipientEmails },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async addOrderReportBilagFromLibrary(
      orderID: string,
      tatGroupID: string,
      reportTemplateID: string,
      bilagLibraryItemID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-bilag-attachments/from-library',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, reportTemplateID, bilagLibraryItemID },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async addOrderReportBilagUpload(
      orderID: string,
      tatGroupID: string,
      reportTemplateID: string,
      attachmentID: string,
      displayTitle: string,
      storagePath: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-bilag-attachments/upload',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, reportTemplateID, attachmentID, displayTitle, storagePath },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async removeOrderReportBilagAttachment(
      orderID: string,
      tatGroupID: string,
      reportTemplateID: string,
      attachmentID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'delete',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-bilag-attachments/{tatGroupID}/{reportTemplateID}/{attachmentID}',
        {
          path: { labAccountID, orderID, tatGroupID, reportTemplateID, attachmentID },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    /**
     * Saves the order of a report's Bilag — the order they are appended to the report PDF. Sends
     * the full ordered ID list in one request; the server applies it in a transaction.
     */
    async reorderOrderReportBilagAttachments(
      orderID: string,
      tatGroupID: string,
      reportTemplateID: string,
      attachmentIDs: string[]
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-bilag-attachments/order',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, reportTemplateID, attachmentIDs },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async resendOrderReports(
      orderID: string,
      tatGroupID: string,
      recipientEmails: string[],
      recreateAllReports: boolean
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/resend-reports',
        {
          path: { labAccountID, orderID },
          body: { tatGroupID, recipientEmails, recreateAllReports },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderReportLocale(orderID: string, reportLocale: string): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/report-locale',
        {
          path: { labAccountID, orderID },
          body: { reportLocale },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateAnalysisComment(
      orderID: string,
      sampleID: string,
      analysisID: string,
      comment: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analyses/{analysisID}/comment',
        {
          path: { labAccountID, orderID, sampleID, analysisID },
          body: { comment },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async addAnalysisAddonLine(
      orderID: string,
      analysisID: string,
      addonID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/analyses/{analysisID}/addon-lines/{addonID}',
        {
          path: { labAccountID, orderID, analysisID, addonID },
        }
      )
      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async removeAnalysisAddonLine(
      orderID: string,
      analysisID: string,
      addonID: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'delete',
        'lab-accounts/{labAccountID}/orders/{orderID}/analyses/{analysisID}/addon-lines/{addonID}',
        {
          path: { labAccountID, orderID, analysisID, addonID },
        }
      )
      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async addComment(orderID: string, text: string): Promise<void> {
      const { currentUser } = useUserStore()
      const entry: IOrderActivityLogComment = {
        id: generateFirestoreId(),
        type: 'comment',
        text,
        createdByUserID: currentUser?.id ?? '',
        timestamps: { createdAt: Date.now() },
      }
      const orderRef = doc(db, 'Apps', '-lab-customer-', 'Orders', orderID)
      await updateDoc(orderRef, { activityLog: arrayUnion(entry) })
    },

    async updateSample(
      labAccountID: string,
      orderID: string,
      sampleID: string,
      analysisIDs: string[],
      sampleAreaID: string,
      tatID: string,
      targetTatGroupID?: string
    ): Promise<void> {
      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analyses',
        {
          path: { labAccountID, orderID, sampleID },
          body: { analysisIDs, sampleAreaID, tatID, targetTatGroupID },
        }
      )
      if (!result.success) throw toRefusalError(result)
    },
  },
})

